import React, { useState, useEffect } from 'react';
import { X, Settings, Monitor, AlertCircle, Check } from 'lucide-react';
import axios from 'axios';
import '../styles/ScreenSettings.css';
import { getBaseUrl } from "../../../config/urls";

type ScreenSettingsProps = {
  roomId: string;
  isOpen: boolean;
  onClose: () => void;
  onSettingsChanged: (settings: ScreenDisplaySettings) => void;
};

export type ScreenDisplaySettings = {
  onlyModerated: boolean;
  displayInterval: number;
  refreshInterval: number;
};

const ScreenSettings: React.FC<ScreenSettingsProps> = ({
  roomId,
  isOpen,
  onClose,
  onSettingsChanged
}) => {
  const [settings, setSettings] = useState<ScreenDisplaySettings>({
    onlyModerated: true,
    displayInterval: 4000,
    refreshInterval: 5000
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // 設定をlocalStorageから読み込み
  useEffect(() => {
    const savedSettings = localStorage.getItem(`screen-settings-${roomId}`);
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      } catch (error) {
        console.error('設定の読み込みに失敗:', error);
      }
    }
  }, [roomId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const previousSettings = localStorage.getItem(`screen-settings-${roomId}`);
      const prevParsed = previousSettings ? JSON.parse(previousSettings) : null;

      // 設定をlocalStorageに保存
      localStorage.setItem(`screen-settings-${roomId}`, JSON.stringify(settings));

      // 承認フィルタが変更された場合、バックエンドの画像リスト状態をリセット
      if (!prevParsed || prevParsed.onlyModerated !== settings.onlyModerated) {
        console.log('[ScreenSettings] 承認フィルタが変更されました。画像リストをリセットします。');
        try {
          const token = localStorage.getItem("token");
          const urlBase = getBaseUrl();
          await axios.post(`${urlBase}/api/picture/resetScreenState?roomId=${roomId}`, {}, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          console.log('[ScreenSettings] バックエンド状態リセット完了');
        } catch (resetError) {
          console.error('[ScreenSettings] バックエンド状態リセット失敗:', resetError);
        }
      }

      // 親コンポーネントに設定変更を通知
      onSettingsChanged(settings);

      // 成功表示
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);

    } catch (error) {
      console.error('設定の保存に失敗:', error);
      alert('設定の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const handleModalKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="screen-settings-overlay" onClick={onClose} onKeyDown={handleModalKeyPress}>
      <div className="screen-settings-modal" onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className="screen-settings-header">
          <div className="screen-settings-title-container">
            <Settings size={20} className="screen-settings-icon" />
            <h3>スクリーン表示設定</h3>
          </div>
          <button className="screen-settings-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* 成功メッセージ */}
        {showSuccess && (
          <div className="screen-settings-success">
            <Check size={16} />
            設定を保存しました
          </div>
        )}

        {/* 設定フォーム */}
        <div className="screen-settings-body">
          {/* 画像表示フィルタ設定 */}
          <div className="setting-section">
            <div className="setting-header">
              <Monitor size={18} />
              <h4>画像表示フィルタ</h4>
            </div>

            <div className="setting-item">
              <div className="setting-toggle">
                <input
                  type="checkbox"
                  id="onlyModerated"
                  checked={settings.onlyModerated}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    onlyModerated: e.target.checked
                  }))}
                />
                <label htmlFor="onlyModerated" className="toggle-label">
                  <span className="toggle-switch"></span>
                  承認済み画像のみ表示
                </label>
              </div>
              <div className="setting-description">
                {settings.onlyModerated ? (
                  <span className="status-approved">
                    <Check size={14} />
                    検閲済みの画像のみが表示されます（安全）
                  </span>
                ) : (
                  <span className="status-warning">
                    <AlertCircle size={14} />
                    すべての画像が表示されます（要注意）
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* フッター */}
        <div className="screen-settings-footer">
          <button onClick={onClose} className="btn-cancel">
            キャンセル
          </button>
          <button
            onClick={handleSave}
            className="btn-save"
            disabled={isSaving}
          >
            {isSaving ? (
              <div className="loading-spinner-small"></div>
            ) : (
              <>
                <Check size={16} />
                設定を保存
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScreenSettings;
